import { requestInvoiceRMSAPI } from "../../../app/api/rmsAuthenticateInvoice";
import { BaseAction } from "../../../core/actions/BaseAction";
import { hideLoader, showLoader } from "../../../Lib/Utils/validationUtil";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { showNotification } from "../../../Services/notificationService";
import { getTranslatedMessage } from "../../../Lib/Utils/i18n";
import * as Sentry from "@sentry/nextjs";

export interface IRequestInvoiceSubmitActionParams {
    bookingId: string;
    bookingRef: string;
    formData: {
        companyName: string;
        trn?: string;
        address1: string;
        address2: string;
        city: string;
        countryBilling: string;
    };
    userEmail: string;
    afterPayment?: boolean;
}

export class RequestInvoiceSubmitAction extends BaseAction<IDefaultActionResult, IRequestInvoiceSubmitActionParams> {

    override async run(): Promise<IDefaultActionResult> {
        let actionResult: IDefaultActionResult = {
            data: [],
            message: "",
            status: "fail"
        };

        try {
            const { bookingId, bookingRef, formData, userEmail, afterPayment } = this.params!;
            if (!afterPayment) {
                showLoader();
            }

            const invoiceResponse = await requestInvoiceRMSAPI(bookingRef, formData, userEmail);
            if (invoiceResponse.success) {
                actionResult.status = "success";
                actionResult.message = getTranslatedMessage("invoiceRequestedSuccessfully");
                actionResult.data = invoiceResponse.data;

                if (!afterPayment) {
                    showNotification("success", actionResult.message, "", 3, "success");
                }
            } else {
                const rawErrorMsg = invoiceResponse.error?.result?.message || "";
                let errorMessage: string;

                if (rawErrorMsg === "Invoice already requested") {
                    errorMessage = getTranslatedMessage("invoiceAlreadyRequested");
                } else {
                    // Capture error in Sentry with context while showing localised message to user
                    Sentry.captureException(new Error("RMS invoice request failed"), {
                        extra: {
                            rmsError: invoiceResponse.error,
                            bookingRef: this.params?.bookingRef,
                        }
                    });
                    errorMessage = getTranslatedMessage("invoiceRequestFailed");
                }

                actionResult.message = errorMessage;
                if (!afterPayment) {
                    showNotification("error", errorMessage, "", 3, "api-error");
                }
            }

        } catch (error) {
            // Capture exception in Sentry with context while showing localised message to user
            Sentry.captureException(error, {
                extra: {
                    bookingRef: this.params?.bookingRef,
                    context: "RequestInvoiceSubmitAction"
                }
            });
            actionResult.message = getTranslatedMessage("invoiceRequestFailed");
            if (!this.params?.afterPayment) {
                showNotification("error", actionResult.message, "", 3, "api-error");
            }
        } finally {
            if (!this.params?.afterPayment) {
                hideLoader()
            }
        }

        return actionResult;
    }
}

export const requestInvoiceSubmitAction = new RequestInvoiceSubmitAction();
